// /app/frontend/src/pages/RequestSamplePage.jsx
// Dedicated page for requesting fabric samples

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Trash2, Loader2, MapPin, User, Mail, Phone,
  ChevronRight, Check, ArrowLeft, Plus, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Toaster } from '../components/ui/sonner';
import { resolveImageUrl } from '../lib/imageCache';
import { brandLabel } from '../lib/brandNames';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RequestSamplePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get samples from localStorage or URL params
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: ''
  });

  useEffect(() => {
    // Load samples from localStorage
    const storedSamples = localStorage.getItem('sampleRequestColors');
    if (storedSamples) {
      try {
        const parsed = JSON.parse(storedSamples);
        setSamples(parsed);
      } catch (e) {
        console.error('Failed to parse stored samples');
      }
    }
    
    // Pre-fill user data if logged in
    const token = localStorage.getItem('apollo_token');
    if (token) {
      fetchUserProfile(token);
    }
  }, []);

  const fetchUserProfile = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = response.data;
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_email: user.email || prev.customer_email,
        customer_phone: user.phone || prev.customer_phone,
        shipping_address: user.address?.street || prev.shipping_address,
        city: user.address?.city || prev.city,
        state: user.address?.state || prev.state,
        zip_code: user.address?.zip || prev.zip_code
      }));
    } catch (error) {
      // User not logged in or error, ignore
    }
  };

  const removeSample = (colorId) => {
    const newSamples = samples.filter(s => s.color_id !== colorId);
    setSamples(newSamples);
    localStorage.setItem('sampleRequestColors', JSON.stringify(newSamples));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (samples.length === 0) {
      toast.error('Please add at least one fabric sample');
      return;
    }
    if (!formData.customer_name || !formData.customer_email) {
      toast.error('Name and email are required');
      return;
    }
    if (!formData.shipping_address || !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Please complete your shipping address');
      return;
    }
    
    setSubmitting(true);
    try {
      // Transform samples to API format
      const colorsPayload = samples.map(s => ({
        id: s.color_id,
        name: s.color_name,
        code: s.color_code || '',
        material_name: s.material_name || '',
        collection_name: s.collection_name || '',
        brand_name: s.brand_name || ''
      }));
      
      const payload = {
        ...formData,
        colors: colorsPayload
      };
      
      await axios.post(`${API_URL}/api/fabric-collections/sample-request`, payload);
      
      // Clear stored samples
      localStorage.removeItem('sampleRequestColors');
      setSamples([]);
      setSubmitted(true);
      toast.success('Sample request submitted! Check your email for confirmation.');
    } catch (error) {
      console.error('Sample request error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center bg-white rounded-2xl p-12 shadow-lg border border-neutral-200"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-neutral-900 mb-4">
                Request Submitted!
              </h1>
              <p className="text-neutral-600 mb-8">
                Your fabric sample request has been received. Sample requests are typically processed and shipped within 2–3 business days, and often ship the next business day. We'll email you tracking information once your samples ship.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/track-samples')}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Track Your Samples
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/products/roller-shades')}
                >
                  Continue Shopping
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/my-favorites')}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  View Favorites
                </Button>
              </div>
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <Toaster position="bottom-right" />
      <Navbar />
      
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="py-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-8 h-8 text-amber-600" />
              <h1 className="text-3xl font-bold text-neutral-900">Request Fabric Samples</h1>
            </div>
            <p className="text-neutral-500">
              Complete the form below to receive free fabric samples at your door.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Samples List - Left Panel */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden sticky top-24">
                <div className="p-4 border-b border-neutral-200 bg-neutral-50">
                  <h2 className="font-semibold text-neutral-900 flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-600" />
                    Selected Samples ({samples.length})
                  </h2>
                </div>
                
                <div className="max-h-[400px] overflow-y-auto">
                  {samples.length === 0 ? (
                    <div className="p-8 text-center text-neutral-500">
                      <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm mb-4">No samples selected</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/products/roller-shades')}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Browse Fabrics
                      </Button>
                    </div>
                  ) : (
                    <AnimatePresence>
                      {samples.map((sample, index) => (
                        <motion.div
                          key={sample.color_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className={`flex items-center gap-3 p-4 ${
                            index !== samples.length - 1 ? 'border-b border-neutral-100' : ''
                          }`}
                        >
                          {/* Swatch */}
                          <div
                            className="w-14 h-14 rounded-lg border border-neutral-200 overflow-hidden flex-shrink-0"
                            style={{ backgroundColor: sample.hex_color || '#E5E5E5' }}
                          >
                            {sample.image_url && (
                              <img
                                src={resolveImageUrl(sample.image_url, 128)}
                                alt={sample.color_name}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-neutral-900 truncate">
                              {sample.color_name}
                            </p>
                            <div className="flex items-center gap-1 text-xs text-neutral-500">
                              <span>{brandLabel(sample.brand_name)}</span>
                              <ChevronRight className="w-3 h-3" />
                              <span className="truncate">{sample.material_name || sample.collection_name}</span>
                            </div>
                            {sample.color_code && <p className="text-[11px] text-neutral-400" data-testid={`sample-code-${sample.color_id}`}>Code {sample.color_code}</p>}
                          </div>
                          
                          {/* Remove */}
                          <button
                            onClick={() => removeSample(sample.color_id)}
                            className="p-2 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  )}
                </div>
                
                {samples.length > 0 && (
                  <div className="p-4 border-t border-neutral-200 bg-neutral-50">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => navigate('/products/roller-shades')}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add More Samples
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Form - Right Panel */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                <div className="p-4 border-b border-neutral-200 bg-neutral-50">
                  <h2 className="font-semibold text-neutral-900">Shipping Information</h2>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Contact Info */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-neutral-700 flex items-center gap-2">
                      <User className="w-4 h-4 text-amber-600" />
                      Contact Information
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name *</Label>
                        <Input
                          value={formData.customer_name}
                          onChange={(e) => handleInputChange('customer_name', e.target.value)}
                          placeholder="John Doe"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) => handleInputChange('customer_email', e.target.value)}
                          placeholder="john@example.com"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Phone (Optional)</Label>
                      <Input
                        type="tel"
                        value={formData.customer_phone}
                        onChange={(e) => handleInputChange('customer_phone', e.target.value)}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  {/* Shipping Address */}
                  <div className="space-y-4 pt-4 border-t border-neutral-100">
                    <h3 className="font-medium text-neutral-700 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-amber-600" />
                      Shipping Address
                    </h3>
                    <div>
                      <Label>Street Address *</Label>
                      <Input
                        value={formData.shipping_address}
                        onChange={(e) => handleInputChange('shipping_address', e.target.value)}
                        placeholder="123 Main St"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="col-span-2 sm:col-span-1">
                        <Label>City *</Label>
                        <Input
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          placeholder="New York"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label>State *</Label>
                        <Input
                          value={formData.state}
                          onChange={(e) => handleInputChange('state', e.target.value)}
                          placeholder="NY"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label>ZIP Code *</Label>
                        <Input
                          value={formData.zip_code}
                          onChange={(e) => handleInputChange('zip_code', e.target.value)}
                          placeholder="10001"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes */}
                  <div className="space-y-2 pt-4 border-t border-neutral-100">
                    <Label>Additional Notes (Optional)</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      placeholder="Any special requests or questions about the fabrics..."
                      rows={3}
                    />
                  </div>
                </div>
                
                {/* Submit */}
                <div className="p-6 border-t border-neutral-200 bg-neutral-50">
                  <Button
                    type="submit"
                    disabled={submitting || samples.length === 0}
                    className="w-full bg-amber-600 hover:bg-amber-700 h-12 text-base"
                    data-testid="submit-sample-request"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : samples.length === 0 ? (
                      'Select samples to continue'
                    ) : (
                      <>
                        <Package className="w-5 h-5 mr-2" />
                        Request {samples.length} Free Sample{samples.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-neutral-500 text-center mt-3">
                    Free shipping within the US. Sample requests are typically processed and shipped within 2–3 business days, and often ship the next business day. Carrier transit time depends on your destination.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default RequestSamplePage;
